import type { CmmdTaskStatus } from "@cmmd-forge/contracts";

export interface StatusConfig {
  readonly status: CmmdTaskStatus;
  readonly label: string;
  /** Tailwind dot color class e.g. "bg-success" or a fixed status hue. */
  readonly dotColor: string;
  /** Tailwind ring/border color e.g. "border-success-border bg-success-surface" */
  readonly circleClass: string;
}

export const STATUS_CONFIGS: readonly StatusConfig[] = [
  {
    status: "backlog",
    label: "Backlog",
    dotColor: "bg-hue-slate",
    circleClass: "border-hue-slate-border bg-hue-slate-surface",
  },
  {
    status: "todo",
    label: "Todo",
    dotColor: "bg-muted-foreground/60",
    circleClass: "border-border",
  },
  {
    status: "in_progress",
    label: "In Progress",
    dotColor: "bg-hue-sky",
    circleClass: "border-hue-sky-border bg-hue-sky-surface",
  },
  {
    status: "in_review",
    label: "In Review",
    dotColor: "bg-hue-violet",
    circleClass: "border-hue-violet-border bg-hue-violet-surface",
  },
  {
    status: "blocked",
    label: "Blocked",
    dotColor: "bg-hue-rose",
    circleClass: "border-hue-rose-border bg-hue-rose-surface",
  },
  {
    status: "done",
    label: "Done",
    dotColor: "bg-success/70",
    circleClass: "border-success-border bg-success-surface",
  },
  {
    status: "cancelled",
    label: "Cancelled",
    dotColor: "bg-muted-foreground/30",
    circleClass: "border-border/50 bg-muted/50",
  },
];

/** Returns the config for a given status string, falling back to "todo" if unknown */
export function getStatusConfig(status: string | null | undefined): StatusConfig {
  return (
    STATUS_CONFIGS.find((config) => config.status === status) ??
    STATUS_CONFIGS.find((config) => config.status === "todo")!
  );
}

/**
 * Maps a WorkItem to its display status for list/board grouping.
 *
 * CMMD tasks: use item.status directly (already a CmmdTaskStatus string).
 * Local threads: status is null; derive display category from agentStatus instead.
 * This ensures local threads always appear in the correct column rather than disappearing.
 */
export function getDisplayStatus(item: {
  source: string;
  status: string | null;
  agentStatus: string;
}): string {
  if (item.source.startsWith("cmmd-")) {
    const normalized = item.status?.trim().toLowerCase() ?? "todo";
    if (STATUS_CONFIGS.some((config) => config.status === normalized)) return normalized;
    if (["active", "open", "planned"].includes(normalized)) return "todo";
    if (["complete", "completed", "closed"].includes(normalized)) return "done";
    return "todo";
  }
  switch (item.agentStatus) {
    case "running":
      return "in_progress";
    case "pending-approval":
    case "pending-input":
      return "in_review";
    case "error":
      return "blocked";
    default:
      return "todo";
  }
}
